import { zodResolver } from "@hookform/resolvers/zod";
import { VisibilityOffOutlined, VisibilityOutlined } from "@mui/icons-material";
import { IconButton, Stack, Typography, useTheme } from "@mui/material";
import { useUserManagementServiceV1 } from "@rw1/api-services";
import { UserManagement_V1 } from "@rw1/auto-generated-clients";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import * as countries from "country-list";
import { FC, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import {
  ConfirmationDialog,
  Dropdown,
  LabeledTextField,
  TextFieldCustomLabel,
} from "../../../components";
import { useInputFontSize } from "../../../hooks";
import { useIsMobile } from "../../../hooks/useIsMobile";
import { useLocalization } from "../../../hooks/useLocalization";
import { USERS_QUERY_KEY } from "../hooks/useUsers";
import { CreateUserFormData, createUserSchema } from "./createUserSchema";
import { ErrorDisplay } from "./ErrorDisplay";
import {
  validateEmail,
  validateJobTitle,
  validateName,
} from "./passwordValidationHelpers";
import { PasswordValidationOverview } from "./PasswordValidationOverview";
import { CreateUserFormProps } from "./types";

const languageOptions = [
  { value: "de", label: "Deutsch (German)" },
  { value: "en", label: "English" },
  { value: "zh", label: "简体中文 (Simplified Chinese)" },
  { value: "fr", label: "Français (French) - (for supported content only)" },
  { value: "ja", label: "日本語 (Japanese) - (for supported content only)" },
  { value: "ko", label: "한국어 (Korean) - (for supported content only)" },
];

const countryOptions = [
  { value: "", label: "Select a country" },
  ...countries
    .getData()
    .map((country) => ({
      value: country.code,
      label: country.name,
    }))
    .sort((a, b) => a.label.localeCompare(b.label)),
];

export const CreateDemoUserForm: FC<CreateUserFormProps> = ({
  demoEmailDomain,
  setIsAddUserDialogOpen,
  setIsSuccessDialogOpen,
  setSuccessMessage,
}) => {
  const theme = useTheme();
  const { t } = useLocalization();
  const [showPassword, setShowPassword] = useState(false);
  const countryStackRef = useRef<HTMLDivElement>(null);
  const emailInputRef = useRef<HTMLDivElement>(null);

  const isMobile = useIsMobile();
  const actionText = useMemo(
    () =>
      isMobile
        ? t("users.createDemoUserForm.create")
        : t("users.createDemoUserForm.createAccount"),
    [isMobile, t],
  );

  const userManagementClient = useUserManagementServiceV1();
  const queryClient = useQueryClient();
  const { companyId } = useParams();

  const createUserMutation = useMutation({
    mutationFn: (userData: UserManagement_V1.FullUserForCreate) => {
      if (!companyId) {
        throw new Error("Company ID not found");
      }

      return userManagementClient.createUser(userData);
    },
    onSuccess: () => {
      form.reset();
      void queryClient.invalidateQueries({ queryKey: [USERS_QUERY_KEY] });
      setIsSuccessDialogOpen(true);
      setSuccessMessage(t("users.createDemoUserForm.successMessage"));
      setIsAddUserDialogOpen(false);
    },
  });

  const form = useForm<CreateUserFormData>({
    resolver: zodResolver(createUserSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      jobTitle: "",
      country: "",
      language: "en",
      password: "",
    },
  });

  const watchedPassword = form.watch("password");

  const watchedFields = form.watch([
    "firstName",
    "lastName",
    "email",
    "language",
    "password",
  ]);
  const [firstName, lastName, email, language, password] = watchedFields;

  const emailFontSize = useInputFontSize(
    `${email}${demoEmailDomain}`,
    emailInputRef,
  );

  const areRequiredFieldsFilled = useMemo(() => {
    return (
      firstName.trim() !== "" &&
      lastName.trim() !== "" &&
      email.trim() !== "" &&
      language.trim() !== "" &&
      password.trim() !== ""
    );
  }, [firstName, lastName, email, language, password]);

  const passwordCriteria = useMemo(() => {
    return {
      minLength: watchedPassword.length >= 8,
      lowercase: /[a-z]/.test(watchedPassword),
      uppercase: /[A-Z]/.test(watchedPassword),
      number: /\d/.test(watchedPassword),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(watchedPassword),
    };
  }, [watchedPassword]);

  const passwordRestrictions = useMemo(() => {
    return {
      invalidCharacter: /[^a-zA-Z0-9!@#$%^&*(),.?":{}|<>]/.test(
        watchedPassword,
      ),
      containsJobTitle: validateJobTitle(
        form.getValues("jobTitle"),
        watchedPassword,
      ),
      containsEmail: validateEmail(email, watchedPassword),
      containsName: validateName(firstName, lastName, watchedPassword),
    };
  }, [watchedPassword, firstName, lastName, email, form]);

  const arePasswordCriteriaMet = Object.values(passwordCriteria).every(Boolean);

  const arePasswordRestrictionsViolated =
    Object.values(passwordRestrictions).some(Boolean);

  const isFormValid =
    areRequiredFieldsFilled &&
    arePasswordCriteriaMet &&
    !arePasswordRestrictionsViolated;

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleFormSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    void form.handleSubmit(onSubmit)();
  };

  const onSubmit = (data: CreateUserFormData) => {
    const formattedEmail = `${data.email}${demoEmailDomain}`;
    const countryCode =
      data.country && data.country.length === 2 ? data.country : "ZZ";

    const createUserRequest = new UserManagement_V1.FullUserForCreate({
      forename: data.firstName,
      surname: data.lastName,
      emailAddress: formattedEmail,
      password: data.password,
      jobTitle: data.jobTitle ?? undefined,
      country: countryCode,
      language: data.language,
      companyId: companyId ?? "",
      role: UserManagement_V1.UserRole.User,
      userId: crypto.randomUUID(),
      username: formattedEmail,
    });

    createUserMutation.mutate(createUserRequest);
  };

  const handleCancel = () => {
    form.reset();
    setIsAddUserDialogOpen(false);
  };

  return (
    <>
      <ConfirmationDialog.Content
        sx={{
          paddingBottom: "0px",
          scrollBehavior: "auto !important",
          "& *": {
            scrollBehavior: "auto !important",
          },
        }}
      >
        <form autoComplete="off" noValidate onSubmit={handleFormSubmit}>
          <LabeledTextField
            {...form.register("firstName")}
            error={!!form.formState.errors.firstName}
            helperText={form.formState.errors.firstName?.message}
            label={t("users.createDemoUserForm.firstName")}
            maxLength={50}
            placeholder={t("users.createDemoUserForm.firstNamePlaceholder")}
            required
          />
          <LabeledTextField
            {...form.register("lastName")}
            error={!!form.formState.errors.lastName}
            helperText={form.formState.errors.lastName?.message}
            label={t("users.createDemoUserForm.lastName")}
            maxLength={50}
            placeholder={t("users.createDemoUserForm.lastNamePlaceholder")}
            required
          />
          <LabeledTextField
            {...form.register("email")}
            InputProps={{
              ref: emailInputRef,
              endAdornment: (
                <Typography
                  sx={{
                    color: theme.colors.text[0],
                    fontSize: emailFontSize,
                    width: "fit-content",
                    flexShrink: 0,
                    lineHeight: "normal",
                  }}
                >
                  {demoEmailDomain}
                </Typography>
              ),
              style: {
                fontSize: emailFontSize,
              },
            }}
            error={!!form.formState.errors.email}
            helperText={form.formState.errors.email?.message}
            label={t("users.createDemoUserForm.emailAddress")}
            placeholder={t("users.createDemoUserForm.emailPlaceholder")}
            required
            type="email"
          />
          <LabeledTextField
            {...form.register("jobTitle")}
            error={!!form.formState.errors.jobTitle}
            helperText={form.formState.errors.jobTitle?.message}
            label={t("users.createDemoUserForm.jobTitle")}
            maxLength={50}
            placeholder={t("users.createDemoUserForm.jobTitlePlaceholder")}
          />

          <Stack
            ref={countryStackRef}
            sx={{ mb: 2, width: "100%", scrollBehavior: "auto !important" }}
          >
            <TextFieldCustomLabel
              label={t("users.createDemoUserForm.country")}
            />
            <Dropdown
              {...form.register("country")}
              MenuProps={{
                PaperProps: {
                  sx: {
                    maxHeight: 250,
                    overflowY: "auto",
                    maxWidth: countryStackRef.current?.offsetWidth,
                    scrollBehavior: "auto !important" as "auto",
                  },
                },
                anchorOrigin: {
                  vertical: "bottom",
                  horizontal: "left",
                },
                transformOrigin: {
                  vertical: "top",
                  horizontal: "left",
                },
                TransitionProps: {
                  timeout: 0,
                },
              }}
              SelectDisplayProps={{
                onFocus: (e) => {
                  e.preventDefault();
                },
              }}
              displayEmpty
              error={!!form.formState.errors.country}
              onChange={(event) => {
                form.setValue("country", event.target.value as string);
              }}
              options={countryOptions}
              sx={{
                maxWidth: undefined,
                scrollBehavior: "auto !important" as "auto",
              }}
              value={form.watch("country")}
            />
          </Stack>

          <Stack sx={{ mb: 2, scrollBehavior: "auto !important" }}>
            <TextFieldCustomLabel
              label={t("users.createDemoUserForm.language")}
              required
            />
            <Dropdown
              {...form.register("language")}
              error={!!form.formState.errors.language}
              onChange={(event) => {
                form.setValue("language", event.target.value as string);
              }}
              options={languageOptions}
              sx={{
                maxWidth: undefined,
                scrollBehavior: "auto !important" as "auto",
              }}
              value={form.watch("language")}
            />
          </Stack>

          <LabeledTextField
            {...form.register("password")}
            InputProps={{
              endAdornment: (
                <IconButton
                  aria-label="toggle password visibility"
                  edge="end"
                  onClick={handleTogglePasswordVisibility}
                  sx={{
                    color: theme.colors.text[6],
                    marginRight: "4px",
                  }}
                >
                  {showPassword ? (
                    <VisibilityOffOutlined />
                  ) : (
                    <VisibilityOutlined />
                  )}
                </IconButton>
              ),
            }}
            error={!!form.formState.errors.password}
            helperText={form.formState.errors.password?.message}
            label={t("users.createDemoUserForm.password")}
            placeholder={t("users.createDemoUserForm.passwordPlaceholder")}
            required
            type={showPassword ? "text" : "password"}
          />

          <PasswordValidationOverview
            passwordCriteria={passwordCriteria}
            passwordRestrictions={passwordRestrictions}
          />
          <button
            disabled={!isFormValid || createUserMutation.isPending}
            style={{ display: "none" }}
            type="submit"
          />
        </form>
      </ConfirmationDialog.Content>
      <ConfirmationDialog.Actions sx={{ position: "relative" }}>
        <ErrorDisplay
          bottom={50}
          error={createUserMutation.error}
          left={28}
          showErrorMessage
          title={t("users.createDemoUserForm.errorTitle")}
        />
        <ConfirmationDialog.Button
          onClick={handleCancel}
          sx={{
            color: theme.colors.primary[5],
          }}
          variant="secondary"
        >
          {t("users.createDemoUserForm.cancel")}
        </ConfirmationDialog.Button>
        <ConfirmationDialog.Button
          disabled={!isFormValid || createUserMutation.isPending}
          isLoading={createUserMutation.isPending}
          loadingText={t("users.createDemoUserForm.creating")}
          onClick={handleFormSubmit}
          type="submit"
          variant="contained"
        >
          {actionText}
        </ConfirmationDialog.Button>
      </ConfirmationDialog.Actions>
    </>
  );
};
